import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/ui/input';
import { Badge, StatusBadge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { useAdminList } from '@/lib/useAdminList';
import { ListFooter } from '@/components/ListFooter';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';

interface Row {
  id: string;
  title: string | null;
  sportType: string;
  status: string;
  date: string;
  time: string;
  orgId: string | null;
  orgName: string | null;
  isRated: boolean;
  isPublic: boolean;
  locationText: string | null;
  maxParticipants: number | null;
  createdAt: string;
  signupsCount: number;
  participantsCount: number;
}

const STATUS_OPTIONS = ['draft', 'open', 'closed', 'finished', 'cancelled'] as const;
type Status = (typeof STATUS_OPTIONS)[number];

function fallbackTitle(r: Row) {
  if (r.title && r.title.trim()) return r.title;
  return `${r.sportType} · ${r.date}`;
}

export function ActivitiesListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [statusSet, setStatusSet] = useState<Set<Status>>(new Set());
  const [sport, setSport] = useState('');
  const [orgFilter, setOrgFilter] = useState<'all' | 'personal' | 'custom'>('all');
  const [orgIdCustom, setOrgIdCustom] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isRated, setIsRated] = useState<'all' | 'true' | 'false'>('all');
  const [hasResults, setHasResults] = useState<'all' | 'true' | 'false'>('all');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const statusCsv = useMemo(() => Array.from(statusSet).join(','), [statusSet]);

  // org_id: 'null' — не пустое значение, а осмысленный фильтр «личные игры,
  // без клуба», поэтому его нельзя отбрасывать вместе с пустыми параметрами.
  const orgIdParam =
    orgFilter === 'personal' ? 'null'
    : orgFilter === 'custom' && orgIdCustom.trim() ? orgIdCustom.trim()
    : undefined;

  const {
    rows, total, loaded, isLoading,
    hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useAdminList<Row>(
    [
      'admin', 'activities',
      debounced, statusCsv, sport, orgFilter, orgIdCustom,
      dateFrom, dateTo, isRated, hasResults,
    ],
    '/api/admin/activities',
    {
      q: debounced,
      status: statusCsv,
      sport,
      org_id: orgIdParam,
      date_from: dateFrom,
      date_to: dateTo,
      is_rated: isRated === 'all' ? undefined : isRated,
      has_results: hasResults === 'all' ? undefined : hasResults,
    },
  );

  const toggleStatus = (s: Status) => {
    setStatusSet((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const columns: ColumnDef<Row>[] = [
    {
      id: 'title',
      header: 'Игра',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="text-zinc-100 font-medium">{fallbackTitle(row.original)}</span>
          {row.original.locationText ? (
            <span className="text-xs text-zinc-500 truncate max-w-[18rem]">
              {row.original.locationText}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: 'sportType',
      header: 'Спорт',
      cell: (c) => <Badge variant="secondary">{c.getValue() as string}</Badge>,
    },
    {
      accessorKey: 'status',
      header: 'Статус',
      cell: (c) => <StatusBadge status={c.getValue() as string} />,
    },
    {
      id: 'datetime',
      header: 'Дата',
      cell: ({ row }) => (
        <div className="flex flex-col text-sm">
          <span className="font-mono tabular-nums text-zinc-300">{row.original.date}</span>
          <span className="font-mono text-xs tabular-nums text-zinc-500">{row.original.time?.slice(0, 5)}</span>
        </div>
      ),
    },
    {
      id: 'org',
      header: 'Клуб',
      cell: ({ row }) =>
        row.original.orgId == null ? (
          <Badge variant="outline">personal</Badge>
        ) : (
          <span className="text-sm text-zinc-300">
            {row.original.orgName ?? <span className="font-mono text-xs">{row.original.orgId.slice(0, 8)}</span>}
          </span>
        ),
    },
    {
      id: 'counts',
      header: 'Записи / Игроки',
      cell: ({ row }) => (
        <span className="font-mono tabular-nums text-sm text-zinc-300">
          {row.original.signupsCount} / {row.original.participantsCount}
          {row.original.maxParticipants ? (
            <span className="text-zinc-600"> / {row.original.maxParticipants}</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'isRated',
      header: 'ELO',
      cell: ({ row }) => row.original.isRated ? <Badge variant="default">ELO</Badge> : null,
    },
  ];


  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50">Игры</h1>
          <span className="text-xs text-zinc-500">
            Найдено: <span className="font-mono tabular-nums text-zinc-300">{total}</span>
          </span>
        </div>
      </div>

      {/* Filter card */}
      <div className="rounded-lg border border-zinc-800/80 bg-bg-2 p-3 space-y-3">
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative flex-1 min-w-0 sm:min-w-[16rem] max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="title / location text…"
              className="pl-9"
            />
          </div>
          <Input
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            placeholder="sport (volleyball)"
            className="w-full sm:max-w-[12rem]"
          />
          <Select
            value={orgFilter}
            onValueChange={(v) => setOrgFilter(v as typeof orgFilter)}
          >
            <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все игры</SelectItem>
              <SelectItem value="personal">Только личные</SelectItem>
              <SelectItem value="custom">Клуб по UUID…</SelectItem>
            </SelectContent>
          </Select>
          {orgFilter === 'custom' && (
            <Input
              value={orgIdCustom}
              onChange={(e) => setOrgIdCustom(e.target.value)}
              placeholder="org_id (uuid)"
              className="w-full sm:max-w-[20rem] font-mono text-xs"
            />
          )}
          <Select
            value={isRated}
            onValueChange={(v) => setIsRated(v as 'all' | 'true' | 'false')}
          >
            <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Рейтинг: любой</SelectItem>
              <SelectItem value="true">Только рейтинговые</SelectItem>
              <SelectItem value="false">Только обычные</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={hasResults}
            onValueChange={(v) => setHasResults(v as 'all' | 'true' | 'false')}
          >
            <SelectTrigger className="w-full sm:w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Результаты: любые</SelectItem>
              <SelectItem value="true">Есть результаты</SelectItem>
              <SelectItem value="false">Без результатов</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 items-center flex-wrap pt-1 border-t border-zinc-900">
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium pr-1">
            Статус
          </div>
          {STATUS_OPTIONS.map((s) => {
            const active = statusSet.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={cn(
                  'px-2.5 py-1 rounded-md border text-[11px] font-medium uppercase tracking-wider transition-colors',
                  active
                    ? 'bg-zinc-100 text-zinc-900 border-zinc-100'
                    : 'border-zinc-800 bg-bg-2 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200',
                )}
              >
                {s}
              </button>
            );
          })}
          <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="flex-1 sm:flex-none sm:w-[11rem]"
            />
            <span className="text-zinc-600">→</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="flex-1 sm:flex-none sm:w-[11rem]"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={(r) => navigate({ to: '/activities/$id', params: { id: r.id } })}
            empty="Игр не найдено"
          />
          <ListFooter
            loaded={loaded}
            total={total}
            hasMore={!!hasNextPage}
            isLoading={isFetchingNextPage}
            onMore={() => void fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}
